import Vue, { reactive } from 'vue';

import {
    ApiException,
    clients,
    DeliveryRun,
    DeliveryProcessDefinition,
    IDeliveryRunClient,
    Product,
    ProductSpecification
} from '@/api';
import { stores } from '@/store';
import { IDeliveryProcessDefinitionStore } from '@/store/deliveryProcessDefinition.store';
import { IProductSpecificationStore } from '@/store/productSpecification.store';

export interface CreateDelivery {
    file?: File;
    definition?: DeliveryProcessDefinition;
    products?: Product[];
}

export class UploadError extends Error {
    private _originalError: Error;

    public get originalError(): Error {
        return this._originalError;
    }

    /**
     *
     */
    constructor(originalError: Error) {
        super();
        this.name = 'UploadError';
        this._originalError = originalError;
    }
}

export class DeliveryCreateService {
    private _processDelivery: DeliveryRun | undefined;

    public state = reactive({ delivery: {}, uploadProgress: 0, loading: false });

    constructor(
        private deliveryClient: IDeliveryRunClient,
        private definitionStore: () => IDeliveryProcessDefinitionStore,
        private productSpecificationStore: () => IProductSpecificationStore
    ) {}

    get definitions(): DeliveryProcessDefinition[] {
        return this.definitionStore().definitions;
    }

    get productspecifications(): ProductSpecification[] {
        return this.productSpecificationStore().specifications;
    }

    get delivery(): CreateDelivery {
        return this.state.delivery;
    }

    get uploadProgress(): number {
        return this.state.uploadProgress;
    }

    get loading(): boolean {
        return this.state.loading;
    }

    get deliveryCreated(): boolean {
        return !!this._processDelivery;
    }

    setFile(file: File): void {
        this.state.delivery = { ...this.delivery, file };
    }
    setDefinition(definitionId?: number): void {
        this.state.delivery = {
            ...this.delivery,
            definition: this.definitions.find((p) => p.id == definitionId) || undefined,
        };
    }
    setProducts(products: Product[]): void {
        this.state.delivery = { ...this.delivery, products };
    }

    setDelivery(payload: CreateDelivery): void {
        this.setDefinition(payload.definition?.id);
        this.setFile(payload.file);
        this.setProducts(payload.products);
    }

    async create(delivery: CreateDelivery): Promise<DeliveryRun> {
        this.setDelivery(delivery);
        this.state.loading = true;
        try {
            this._processDelivery = await this.deliveryClient.createDelivery({
                definitionId: this.delivery.definition?.id,
                products: this.delivery.products,
            });
            // }
            await this.uploadDelivery();
            this.state.loading = false;
            return this._processDelivery;
        } catch (e) {
            this.state.loading = false;
            throw e;
        } finally {
            this.clearState();
        }
    }

    async cancel(): Promise<void> {
        if (this._processDelivery?.id) {
            return await this.deliveryClient.cancel(this._processDelivery?.id);
        }
        return;
    }

    async uploadDelivery(): Promise<void> {
        if (this._processDelivery?.id) {
            this.state.uploadProgress = 0;
            try {
                await this.deliveryClient.upload(
                    this._processDelivery?.id,
                    { data: this.delivery.file, fileName: this.delivery.file?.name || '' },
                    {
                        onUploadProgress: (e: { loaded: number; total?: number }) =>
                            (this.state.uploadProgress = Math.round((e.loaded * 100) / e.total)),
                    },
                );
            } catch (e) {
                if (e instanceof ApiException) {
                    if ((e as ApiException).status === 400 || (e as ApiException).status === 413) {
                        throw e;
                    }
                }
                throw new UploadError(e as Error);
            }
        }
    }

    async init(): Promise<void> {
        this._processDelivery = undefined;
        await this.definitionStore().fetchDefinitions();
        await this.productSpecificationStore().fetchSpecifications();
    }

    clearState() {
        this.state = { delivery: {}, uploadProgress: 0, loading: false };
    }
}

export const DeliveryCreateServiceInstance = new DeliveryCreateService(
    clients.DeliveryRunClient,
    stores.DeliveryProcessDefinitionStore,
    stores.ProductSpecificationStore
);
